import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { api } from '../../utils/requestInterceptor';
import { toast } from 'react-hot-toast';
import { renderContentWithMentions } from '../../utils/mentions';

const ClientPortalNotes = ({ project, projectId }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (project?.id) {
      // If we have a project object, extract notes from it
      extractNotesFromProject();
    } else if (projectId) {
      // If we only have a projectId, fetch updates from API
      fetchProjectUpdates();
    }
  }, [project, projectId]);
  
  // Extract notes from project object
  const extractNotesFromProject = () => {
    try {
      setLoading(true);
      setError(null);
      
      // Extract all notes from project data
      const allNotes = [];
      
      if (project && project.phases) {
        project.phases.forEach(phase => {
          if (phase.steps) {
            phase.steps.forEach(step => {
              if (step.notes && step.notes.length > 0) {
                // Add phase and step information to each note
                const stepNotes = step.notes.map(note => ({
                  ...note,
                  step_name: step.name,
                  phase_name: phase.name,
                  step_id: step.id,
                  phase_id: phase.id
                }));
                allNotes.push(...stepNotes);
              }
            });
          }
        });
      }
      
      // Sort notes by creation date (newest first)
      allNotes.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      setNotes(allNotes);
    } catch (err) {
      console.error('Error processing project notes:', err);
      setError('Failed to load project notes. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch project updates directly from the API
  const fetchProjectUpdates = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch project updates from the API
      const response = await axios.get(`/api/client-portal/projects/${projectId}/updates`);
      
      if (response.data && response.data.updates) {
        setNotes(response.data.updates);
      } else {
        setNotes([]);
      }
    } catch (err) {
      console.error('Error fetching project updates:', err);
      setError('Failed to load project updates. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to format dates
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading && notes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48">
        <div className="w-10 h-10 border-t-2 border-b-2 border-gray-500 rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-600">Loading updates...</p>
      </div>
    );
  }
  
  if (error && notes.length === 0) {
    return (
      <div className="p-4 text-red-700 bg-red-100 rounded-md">
        <p>{error}</p>
        <button 
          onClick={project ? extractNotesFromProject : fetchProjectUpdates}
          className="mt-2 text-red-700 underline"
        >
          Retry
        </button>
      </div>
    );
  }
  
  return (
    <div className="bg-white shadow-sm rounded-lg p-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Project Updates</h2>
      </div>
      
      {/* Notes list */}
      {loading && (
        <div className="flex justify-center py-4">
          <div className="w-8 h-8 border-t-2 border-b-2 border-gray-500 rounded-full animate-spin"></div>
        </div>
      )}
      
      {notes.length > 0 ? (
        <div className="space-y-4">
          {notes.map((note) => (
            <div key={note.id} className="bg-gray-50 overflow-hidden rounded-md p-4">
              <div className="flex justify-between items-start">
                <div className="flex-grow">
                  <div className="text-sm text-gray-900 rich-text-content">
                    {renderContentWithMentions(note.content)}
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    Posted on {formatDate(note.created_at)}
                    {note.created_by_name && (
                      <span className="ml-2">by {note.created_by_name}</span>
                    )}
                  </div>
                </div>
              </div>
              {(note.phase_name || note.step_name) && (
                <div className="mt-2 flex items-center space-x-2 text-xs">
                  {note.phase_name && (
                    <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded font-medium">
                      {note.phase_name}
                    </span>
                  )}
                  {note.phase_name && note.step_name && (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                  {note.step_name && (
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded font-medium">
                      {note.step_name}
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 bg-gray-50 rounded-md">
          <p className="text-gray-500">No updates are available for this project yet.</p>
        </div>
      )}
    </div>
  );
};

export default ClientPortalNotes; 